import { useState, useEffect, Fragment } from "react";
import {
  Button,
  Card,
  Table,
  Container,
  Row,
  Col,
  Input,
  CardFooter,
} from "reactstrap";
import { reportmanageService } from "../../../services/reportmanageService";
import "../../../assets/css/custom.css";
import Header from "components/Headers/Header.js";
import Paginationcomponent from "components/Paginationcomponent.js";
import { Formloader } from "components/Loader/PageLoader.js";

// The three audit teams, then the four individual phases BRD V2 adds as rows
// of their own. Must match PhaseTeams::reportGroups() on the backend.
const TEAMS = [
  "Support",
  "First Audit",
  "Second Audit",
  "File Collation",
  "Financial statement production",
  "Spreadsheet jobs",
  "Workpapers",
];

// Every hours figure on this report is working hours on an 8-hour day, so
// "days" and "hours / 8" agree with the working-day gap between the dates --
// which is the reconciliation the report exists to provide.
const COLSPAN = 17;

const PhaseTatReport = (props) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [total_page, setTotalData] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const rowsPerPageOptions = [5, 10, 25, 50, 100];

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [search, setSearch] = useState("");
  const [team, setTeam] = useState("");

  // task_id + team -> hold periods, loaded lazily when a Holds cell is clicked.
  const [expanded, setExpanded] = useState({});
  const [holdData, setHoldData] = useState({});

  // Load the first page once on mount; every later fetch is driven by an
  // explicit Filter / Reset / paging action, not by state changing.
  useEffect(() => {
    get_report_data(1);
  }, []);

  function filters(overrides = {}) {
    return {
      start_date: startDate,
      end_date: endDate,
      search: search,
      team: team,
      ...overrides,
    };
  }

  function get_report_data(page, overrides = {}, perPage = rowsPerPage) {
    setLoading(true);
    reportmanageService
      .getPhaseTatReport(filters(overrides), page, perPage)
      .then((response) => {
        if (response && response.data && response.data.data) {
          const p = response.data.data;
          setJobs(p.data || []);
          setCurrentPage(p.current_page);
          setTotalData(p.total);
        } else {
          setJobs([]);
          setTotalData(0);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }

  function applyFilter() {
    setExpanded({});
    get_report_data(1);
  }

  function resetFilter() {
    setStartDate("");
    setEndDate("");
    setSearch("");
    setTeam("");
    setExpanded({});
    get_report_data(1, { start_date: "", end_date: "", search: "", team: "" });
  }

  function downloadReport() {
    setLoading(true);
    reportmanageService
      .exportPhaseTatReport(filters())
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
  }

  const handlePageClick = (page) => {
    setCurrentPage(page);
    setExpanded({});
    get_report_data(page);
  };

  const handlePreviousClick = () => {
    if (currentPage > 1) handlePageClick(currentPage - 1);
  };

  const handleNextClick = () => {
    if (currentPage < Math.ceil(total_page / rowsPerPage)) handlePageClick(currentPage + 1);
  };

  const handleRowsPerPageChange = (e) => {
    const per = parseInt(e.target.value);
    setRowsPerPage(per);
    setExpanded({});
    get_report_data(1, {}, per);
  };

  function toggleHolds(task_id, teamName) {
    const key = `${task_id}||${teamName}`;
    if (expanded[key]) {
      setExpanded({ ...expanded, [key]: false });
      return;
    }
    setExpanded({ ...expanded, [key]: true });
    if (holdData[key]) return;
    reportmanageService.getPhaseTatHoldDetails(task_id, teamName).then((response) => {
      if (response && response.data && response.data.data) {
        setHoldData((prev) => ({ ...prev, [key]: response.data.data.periods || [] }));
      }
    });
  }

  const formatDateTime = (val) => {
    if (!val) return "-";
    const d = new Date(String(val).replace(" ", "T"));
    if (isNaN(d.getTime())) return val;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${dd}-${mm}-${yyyy} ${hh}:${mi}`;
  };

  // Hours and days together in one cell: the BRD asks for both, and showing
  // them side by side is what lets a reader confirm days == hours / 8.
  const hrsDays = (hours, days) => {
    if (hours === null || hours === undefined || hours === "") return "-";
    return (
      <>
        {hours}
        <span style={{ color: "#8898aa", fontSize: "11px" }}> / {days}d</span>
      </>
    );
  };

  const linkStyle = {
    cursor: "pointer",
    color: "#5e72e4",
    fontWeight: 600,
    textDecoration: "underline",
  };

  const jobRowStyle = {
    background: "#f0f3ff",
    fontWeight: 600,
  };

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <Col>
            <h2>Phase TAT</h2>
            <p style={{ color: "#8898aa", fontSize: "12px", marginBottom: "8px" }}>
              Job lifecycle by phase team. Gross TAT = Completed &minus; Assigned, Net TAT = Gross
              &minus; Hold, Net after R/R = Net + Rework + Reassigned, Queue/Waiting = Started
              &minus; Assigned. Hours are working hours on an 8-hour day, so days = hours
              &divide; 8.
            </p>
          </Col>
        </Row>

        <Row>
          <Col xl="2">
            <label>Start Date</label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </Col>
          <Col xl="2">
            <label>End Date</label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </Col>
          <Col xl="2">
            <label>Phase (Team)</label>
            <Input type="select" value={team} onChange={(e) => setTeam(e.target.value)}>
              <option value="">All Teams</option>
              {TEAMS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Input>
          </Col>
          <Col xl="3">
            <label>Client / Sub Client</label>
            <Input
              type="text"
              placeholder="Search client or sub client"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Col>
          <Col xl="3" style={{ paddingTop: "32px" }}>
            <Button color="primary" size="sm" onClick={applyFilter} disabled={loading}>
              Filter
            </Button>
            <Button color="secondary" size="sm" onClick={resetFilter} disabled={loading}>
              Reset
            </Button>
            <Button
              className="btn-icon-clipboard"
              type="button"
              onClick={downloadReport}
              disabled={loading}
              style={{ marginLeft: "8px", padding: "4px 10px" }}
              title="Download Phase TAT Report"
            >
              <i className="fas fa-download" style={{ fontSize: "12px" }}></i> Export
            </Button>
          </Col>
        </Row>
        <br />

        {loading ? <Formloader /> : null}

        <Row>
          <Col xl="12">
            <Card style={{ padding: "0px", marginBottom: "10px" }}>
              <Table
                className="align-items-center table-flush custom-style"
                responsive
                size="sm"
                style={{ fontSize: "12px" }}
              >
                <thead className="thead-light">
                  <tr>
                    <th className="whitespace">Phase</th>
                    <th className="whitespace">Assigned</th>
                    <th className="whitespace">Started</th>
                    <th className="whitespace">Completed</th>
                    <th className="whitespace text-right">Gross TAT (hrs/days)</th>
                    <th className="whitespace text-right">Holds</th>
                    <th className="whitespace">First Hold Start</th>
                    <th className="whitespace">Last Hold Removal</th>
                    <th className="whitespace text-right">Hold (hrs/days)</th>
                    <th className="whitespace text-right">Net TAT (hrs/days)</th>
                    <th className="whitespace text-right">Reassigned (hrs/days)</th>
                    <th className="whitespace text-right">Rework (hrs/days)</th>
                    <th className="whitespace text-right">Net after R/R (hrs/days)</th>
                    <th className="whitespace text-right">Waiting (hrs/days)</th>
                    <th className="whitespace">Assigned User</th>
                    <th className="whitespace">Completed User</th>
                    <th className="whitespace">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs && jobs.length ? (
                    jobs.map((job) => (
                      // Job header row then its team rows: BRD section 4 asks for the
                      // complete lifecycle, Job Received -> phases -> final completion.
                      <Fragment key={`job-${job.task_id}`}>
                        <tr style={jobRowStyle}>
                          <td className="whitespace" colSpan="4">
                            {job.client_name}
                            {job.sub_client ? ` / ${job.sub_client}` : ""}
                            <span style={{ color: "#8898aa", fontWeight: 400 }}>
                              {" "}
                              &nbsp;Job #{job.task_id} &nbsp;| Received{" "}
                              {formatDateTime(job.job_received)} &nbsp;| Completed{" "}
                              {formatDateTime(job.job_completion)}
                            </span>
                          </td>
                          <td className="text-right">
                            {hrsDays(job.total_gross_hours, job.total_gross_days)}
                          </td>
                          <td colSpan="3"></td>
                          <td className="text-right">
                            {hrsDays(job.total_hold_hours, job.total_hold_days)}
                          </td>
                          <td className="text-right">
                            {hrsDays(job.total_net_hours, job.total_net_days)}
                          </td>
                          <td colSpan="2"></td>
                          <td className="text-right">
                            {hrsDays(job.total_net_after_hours, job.total_net_after_days)}
                          </td>
                          <td colSpan="4" style={{ color: "#8898aa", fontWeight: 400 }}>
                            Job total &mdash; {job.job_status}
                          </td>
                        </tr>

                        {job.teams.map((t) => {
                          const key = `${job.task_id}||${t.team}`;
                          return (
                            <Fragment key={key}>
                              <tr>
                                <td className="whitespace" title={t.phase_names}>
                                  {t.team}
                                </td>
                                <td className="whitespace">{formatDateTime(t.assigned)}</td>
                                <td className="whitespace">{formatDateTime(t.started)}</td>
                                <td className="whitespace">{formatDateTime(t.completed)}</td>
                                <td className="text-right">
                                  {hrsDays(t.gross_hours, t.gross_days)}
                                </td>
                                <td className="text-right">
                                  {t.holds_count > 0 ? (
                                    <span style={linkStyle} onClick={() => toggleHolds(job.task_id, t.team)}>
                                      {t.holds_count}
                                    </span>
                                  ) : (
                                    <span>0</span>
                                  )}
                                </td>
                                <td className="whitespace">{formatDateTime(t.first_hold_start)}</td>
                                <td className="whitespace">{formatDateTime(t.last_hold_end)}</td>
                                <td className="text-right">{hrsDays(t.hold_hours, t.hold_days)}</td>
                                <td className="text-right">{hrsDays(t.net_hours, t.net_days)}</td>
                                <td className="text-right">
                                  {hrsDays(t.reassigned_hours, t.reassigned_days)}
                                </td>
                                <td className="text-right">
                                  {hrsDays(t.rework_hours, t.rework_days)}
                                </td>
                                <td className="text-right">
                                  {hrsDays(t.net_after_hours, t.net_after_days)}
                                </td>
                                <td className="text-right">
                                  {hrsDays(t.waiting_hours, t.waiting_days)}
                                </td>
                                <td className="whitespace">{t.assigned_user || "-"}</td>
                                <td className="whitespace">{t.completed_user || "-"}</td>
                                <td className="whitespace">{t.status}</td>
                              </tr>

                              {expanded[key] ? (
                                <tr>
                                  <td colSpan={COLSPAN} style={{ background: "#fbfbfb" }}>
                                    {holdData[key] ? (
                                      holdData[key].length ? (
                                        <Table size="sm" style={{ fontSize: "11px", marginBottom: 0 }}>
                                          <thead>
                                            <tr>
                                              <th>Phase</th>
                                              <th>Hold Start</th>
                                              <th>Hold Removal</th>
                                              <th className="text-right">Duration (hrs/days)</th>
                                              <th>Reason</th>
                                              <th>Changed By</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {holdData[key].map((h, hi) => (
                                              <tr key={hi}>
                                                <td className="whitespace">{h.phase_name}</td>
                                                <td className="whitespace">
                                                  {formatDateTime(h.hold_start)}
                                                </td>
                                                <td className="whitespace">
                                                  {h.ongoing ? (
                                                    <em>still on hold</em>
                                                  ) : (
                                                    formatDateTime(h.hold_end)
                                                  )}
                                                </td>
                                                <td className="text-right">
                                                  {hrsDays(h.duration_hours, h.duration_days)}
                                                </td>
                                                <td className="whitespace">{h.hold_reason}</td>
                                                <td className="whitespace">{h.changed_by || "-"}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </Table>
                                      ) : (
                                        <span>No hold periods</span>
                                      )
                                    ) : (
                                      <span>Loading hold details…</span>
                                    )}
                                  </td>
                                </tr>
                              ) : null}
                            </Fragment>
                          );
                        })}
                      </Fragment>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={COLSPAN} className="text-center">
                        No records found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
              <CardFooter className="py-4">
                <Paginationcomponent
                  currentPage={currentPage}
                  totalItems={total_page}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={rowsPerPageOptions}
                  handlePageClick={handlePageClick}
                  handlePreviousClick={handlePreviousClick}
                  handleNextClick={handleNextClick}
                  handleRowsPerPageChange={handleRowsPerPageChange}
                />
              </CardFooter>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default PhaseTatReport;
